import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/solid';
import { AuthProvider, useAuth } from './AuthContext';
import Devices from './components/Devices';
import MenuItems from './components/MenuItems';
import Branches from './components/Branches';
import Schedules from './components/Schedules';
import Login from './components/Login';
import PrivateRoute from './PrivateRoute';
import Logout from './components/Logout';
import MysteryReports from './components/MysteryReports';
import RewardStore from './components/RewardStore';
import StreakConfig from './components/StreakConfig';
import TokenEarningConfig from './components/TokenEarningConfig';
import VisitRewardsConfig from './components/VisitRewardsConfig';
import Banners from './components/Banners';
import Popups from './components/Popups';
import TestOtpNumbers from './components/TestOtpNumbers';
import AppUsersCRM from './components/AppUsersCRM';
import CrmDownloads from './components/CrmDownloads';
import VendorUpload from './components/VendorUpload';
import VendorUploadAdmin from './components/VendorUploadAdmin';
import CategoryIcons from './components/CategoryIcons';
import AccessControl from './components/AccessControl';
import { logPageView } from './services/adminApi';
function App() {
  return (
    <Router>
      <AuthProvider>
        <AppLayout>
          <Routes>
            {/* <Route path="/pages/gift-card-bulk" element={<GiftCard />} /> */}
          {/* <Route path="/pages/app-survey" element={<AppSurvey />} /> */}
          {/* <Route path="/matcha" element={<MatchaCounter />} /> */}
          <Route path="/" element={<PrivateRoute permission="tv.menu_items.view"><MenuItems /></PrivateRoute>} />
          <Route path="/devices" element={<PrivateRoute permission="tv.devices.view"><Devices /></PrivateRoute>} />
          <Route path="/branches" element={<PrivateRoute permission="tv.branches.view"><Branches /></PrivateRoute>} />
          <Route path="/schedules" element={<PrivateRoute permission="tv.schedules.view"><Schedules /></PrivateRoute>} />
          <Route path="/rewards" element={<PrivateRoute permission="loyalty.rewards.view"><RewardStore /></PrivateRoute>} />
          <Route path="/earning-rules" element={<PrivateRoute permission="loyalty.earning_rules.view"><TokenEarningConfig /></PrivateRoute>} />
          <Route path="/streaks" element={<PrivateRoute permission="loyalty.streaks.view"><StreakConfig /></PrivateRoute>} />
          <Route path="/visit-rewards" element={<PrivateRoute superadminOnly><VisitRewardsConfig /></PrivateRoute>} />
          <Route path="/banners" element={<PrivateRoute permission="content.banners.view"><Banners /></PrivateRoute>} />
          <Route path="/popups" element={<PrivateRoute permission="content.popups.view"><Popups /></PrivateRoute>} />
          <Route path="/category-icons" element={<PrivateRoute permission="content.category_icons.view"><CategoryIcons /></PrivateRoute>} />
          <Route path="/crm/users" element={<PrivateRoute permission="crm.users.view"><AppUsersCRM /></PrivateRoute>} />
          <Route path="/crm/downloads" element={<PrivateRoute permission="crm.users.export"><CrmDownloads /></PrivateRoute>} />
          <Route path="/test-otp-numbers" element={<PrivateRoute permission="admin.test_otp_numbers.view"><TestOtpNumbers /></PrivateRoute>} />
          <Route path="/mystery-reports" element={<PrivateRoute permission="reports.mystery.view"><MysteryReports /></PrivateRoute>} />
          <Route path="/login" element={<Login />}></Route>
          <Route path="/uploadfile" element={<VendorUpload />} />
          <Route path="/vendor-uploads" element={<PrivateRoute permission="admin.vendor_uploads.view"><VendorUploadAdmin /></PrivateRoute>} />
          <Route path="/access-control" element={<PrivateRoute permissions={['security.audit.view', 'security.sessions.view']}><AccessControl /></PrivateRoute>} />
            {/* <Route path="/sales" element={<SalesListener />}></Route> */}
          </Routes>
        </AppLayout>
      </AuthProvider>
    </Router>
  );
}

// Component for displaying the navigation and layout
function AppLayout({ children }) {
  const authContext = useAuth();
  const { currentUser, isSuperadmin } = authContext;
  const hasPermission = authContext.hasPermission || (() => false);
  const [sidebarOpen, setSidebarOpen] = useState(() => window.innerWidth >= 1280);
  const location = useLocation();

  const navSections = [
    {
      label: 'TV Related',
      links: [
        { to: '/', label: 'Menu Items', permission: 'tv.menu_items.view' },
        { to: '/devices', label: 'Devices', permission: 'tv.devices.view' },
        { to: '/branches', label: 'Branches', permission: 'tv.branches.view' },
        { to: '/schedules', label: 'Schedules', permission: 'tv.schedules.view' },
      ],
    },
    {
      label: 'Rewards',
      links: [
        { to: '/rewards', label: 'Store Rewards', permission: 'loyalty.rewards.view' },
        { to: '/earning-rules', label: 'Earning Rules', permission: 'loyalty.earning_rules.view' },
        { to: '/streaks', label: 'Streak Settings', permission: 'loyalty.streaks.view' },
        { to: '/visit-rewards', label: 'Visit Rewards', superadminOnly: true },
      ],
    },
    {
      label: 'App Content',
      links: [
        { to: '/banners', label: 'Promo Banners', permission: 'content.banners.view' },
        { to: '/popups', label: 'Popups', permission: 'content.popups.view' },
        { to: '/category-icons', label: 'Category Icons', permission: 'content.category_icons.view' },
      ],
    },
    {
      label: 'CRM',
      links: [
        { to: '/crm/users', label: 'App Users', permission: 'crm.users.view' },
        { to: '/crm/downloads', label: 'Downloads', permission: 'crm.users.export' },
      ],
    },
    {
      label: 'Admin',
      links: [
        { to: '/test-otp-numbers', label: 'Test OTP Numbers', permission: 'admin.test_otp_numbers.view' },
        { to: '/vendor-uploads', label: 'Vendor Uploads', permission: 'admin.vendor_uploads.view' },
        { to: '/access-control', label: 'Access & Audit', anyPermissions: ['security.audit.view', 'security.sessions.view'] },
      ],
    },
    {
      label: 'Reports',
      links: [{ to: '/mystery-reports', label: 'Mystery Reports', permission: 'reports.mystery.view' }],
    },
  ];

  const visibleSections = navSections.map((section) => ({
    ...section,
    links: section.links.filter((link) => link.superadminOnly ? isSuperadmin : (isSuperadmin || (link.permission ? hasPermission(link.permission) : link.anyPermissions?.some(hasPermission)))),
  })).filter((section) => section.links.length > 0);

  useEffect(() => {
    if (!currentUser) return;
    const link = navSections.flatMap((section) => section.links).find((item) => item.to === location.pathname);
    if (link?.permission && hasPermission(link.permission)) logPageView(link.permission, location.pathname).catch(() => {});
  // navSections contains static permission metadata and does not affect the page-view side effect.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, location.pathname, hasPermission]);

  const closeSidebar = () => setSidebarOpen(false);
  const handleNavLinkClick = () => {
    if (window.innerWidth < 1280) closeSidebar();
  };

  return (
    <div className="min-h-screen">
      {/* Show the nav bar only if the user is logged in */}
      {currentUser && (
        <>
          <nav className={`bg-gt-white shadow flex justify-center ${location.pathname === '/mystery-reports' ? 'mb-0' : 'mb-4'}`}>
            <div className="container py-3 px-3 flex justify-between items-center">
              <div className="flex items-center gap-3">
                <button
                  className="p-2 text-teal-500 hover:opacity-80"
                  onClick={() => setSidebarOpen(true)}
                  aria-label="Open menu"
                >
                  <Bars3Icon className="h-6 w-6" />
                </button>
                <div className="text-sm md:text-lg font-bold">Got Tea Admin</div>
              </div>
            </div>
          </nav>

          {/* Sidebar overlay */}
          {sidebarOpen && (
            <div
              className="fixed inset-0 bg-black bg-opacity-50 z-40 xl:hidden"
              onClick={closeSidebar}
            />
          )}

          {/* Left sidebar drawer */}
          <div
            className={`fixed top-0 left-0 h-full w-72 max-w-[85vw] bg-gt-white shadow-lg z-50 transform transition-transform duration-300 ease-in-out ${
              sidebarOpen ? 'translate-x-0' : '-translate-x-full'
            }`}
          >
            <div className="p-4 h-full flex flex-col">
              <div className="flex justify-between items-center mb-6">
                <span className="text-lg font-bold">Got Tea Admin</span>
                <button
                  onClick={closeSidebar}
                  className="p-2 text-teal-500 hover:opacity-80"
                  aria-label="Collapse menu"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto space-y-6">
                {visibleSections.map((section) => (
                  <div key={section.label}>
                    <div className="px-3 text-xs font-bold uppercase tracking-wide text-gray-500">
                      {section.label}
                    </div>
                    <div className="mt-2 flex flex-col space-y-1">
                      {section.links.map((link) => (
                        <Link
                          key={link.to}
                          to={link.to}
                          onClick={handleNavLinkClick}
                          className={`text-lg py-2 px-3 rounded ${
                            location.pathname === link.to
                              ? 'bg-teal-500 text-white'
                              : 'text-teal-500 hover:bg-teal-50'
                          }`}
                        >
                          {link.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <div className="pt-4 mt-4 border-t border-gray-200">
                <Logout />
              </div>
            </div>
          </div>
        </>
      )}
      <main className={currentUser && sidebarOpen ? 'xl:ml-72' : ''}>{children}</main>
    </div>
  );
}

export default App;
